// Used in assign-cart-content-builder.component.html

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { MatSelectChange } from "@angular/material/select";
import { Subject } from "rxjs";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { LanguageHelper } from "#helpers/language.helper";
import { AssetId, EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, AssetAssociation, Bundle, Edition, Level, assetTypeIds } from "@carnegie-learning/cure-types";
import { SelectedAssets } from "cli-types";

@Component({
    selector: "lesson-builder-form",
    templateUrl: "./lesson-builder-form.component.html",
    styleUrls: ["./lesson-builder-form.component.scss"],
    standalone: false
})
export class LessonBuilderFormComponent
    implements OnInit, OnChanges, OnDestroy
{
    componentInit = false;
    @Input() userGUID = "";
    @Input() activePrograms: AssetAssociation[] = [];
    @Input() activeEditions: Edition[] = [];
    @Input() convertToLesson = false;
    @Input() assetInUse = false;
    @Input() currentLessonForm?: Bundle;
    @Input() subjectId: SubjectId = "";
    @Input() editionId: EditionId = "";
    @Input() selectedAssets: SelectedAssets;
    @Input() lessonFormValidity = false;
    @Input() editingLesson = false;

    @Output() setLessonAssets = new EventEmitter<Asset[]>();
    @Output() addLessonAssets = new EventEmitter<void>();
    @Output() setCurrentLessonForm = new EventEmitter<Bundle>();
    @Output() create = new EventEmitter<Bundle>();
    @Output() update = new EventEmitter<Bundle>();
    @Output() cancel = new EventEmitter<void>();
    @Output() setLessonFormData = new EventEmitter<Bundle>();
    @Output() setLessonFormValidity = new EventEmitter<boolean>();

    exists = false;
    lessonAssetId: AssetId = "";
    lessonUnits: string[] = [];
    lessonLevels: Level[] = [];
    levelLabel = "Level";
    working = false;
    firstTimeLoading = true;
    lessonIcon = "";
    selectedEdition: AssetAssociation;

    lessonForm = this.fb.group({
        assetId: ["", []],
        displayName: ["", [Validators.required, this.noWhitespaceValidator]],
        description: ["", [Validators.required, this.noWhitespaceValidator]],
        levelIds: [[], [Validators.required]],
        bundledAssetIds: [[], []],
        unit: ["", []],
        subjectId: ["", [Validators.required]],
        editionIds: [[], [Validators.required]],
        publishedBy: ["", Validators.required],
        visibility: ["self", Validators.required],
        audiences: [["TCH", "STU"], Validators.required],
        locales: [["en_US"], Validators.required],
        scorePossible: [
            "",
            [
                Validators.required,
                this.formHelper.removeInputWhiteSpaces,
                this.formHelper.scorePossibleValidator,
            ],
        ],
        assetTypeId: [assetTypeIds.BUNDLE, [Validators.required]],
        assetActivityTypeId: [assetTypeIds.BUNDLE, [Validators.required]],
        isResource: [false, Validators.required],
        isAssignable: [true, Validators.required],
        isDeleted: [false, Validators.required],
        audit: [{ createdBy: "" }, Validators.required],
    });

    private ngUnsubscribe = new Subject();

    constructor(
        private fb: UntypedFormBuilder,
        private languageHelper: LanguageHelper,
        private formHelper: FormHelper
    ) {}

    ngOnInit() {
        this.firstTimeLoading = true;
        this.lessonIcon =
            this.currentLessonForm &&
            this.languageHelper.ifMathLanguage(this.currentLessonForm.subjectId)
                ? "lesson-math"
                : "lesson";
        this.lessonForm.patchValue({ editionIds: [this.editionId] });
    }

    // For Description, do not allow just whitespace
    noWhitespaceValidator(control: UntypedFormControl) {
        const plainText = control.value?.replace(/<[^>]*>/g, "");
        const isWhitespace =
            ((plainText && plainText.toString()) || "").trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }

    ngOnChanges(changes: SimpleChanges) {
        const edition = this.activeEditions.find(
            (e) => e.editionId === this.editionId
        );
        if (edition) {
            this.selectedEdition = edition;
            this.subjectId = edition.subjectId;
        }
        this.levelLabel = this.languageHelper.gradeLevelReturn(this.subjectId);

        if (this.currentLessonForm && this.currentLessonForm.assetId) {
            this.exists = true;

            this.lessonAssetId = this.currentLessonForm.assetId;
            this.lessonForm.patchValue({
                assetId: this.currentLessonForm.assetId,
                displayName: this.currentLessonForm.displayName,
                audiences: this.currentLessonForm.audiences,
                editionIds: this.currentLessonForm.editionIds,
                description: this.currentLessonForm.description,
                subjectId: this.currentLessonForm.subjectId,
                assetTypeId: this.currentLessonForm.assetTypeId,
                scorePossible: this.currentLessonForm.scorePossible,
                visibility: this.currentLessonForm.visibility,
                audit: this.currentLessonForm.audit,
                publishedBy: this.currentLessonForm.publishedBy,
                levelIds: this.currentLessonForm.levelIds + "",
                bundledAssetIds: this.currentLessonForm.bundledAssetIds,
                unit: this.currentLessonForm.unit,
            });
            if (this.currentLessonForm.subjectId) {
                this.subjectId = this.currentLessonForm.subjectId;
            }

            const currentSelectedAssets: Asset[] = [];
            const tasks = this.currentLessonForm.tasks;

            if (tasks && tasks.length > 0) {
                tasks.forEach(function (asset) {
                    if (asset) {
                        currentSelectedAssets.push(asset);
                    }
                });
            }

            if (!this.firstTimeLoading) {
                this.setLessonAssets.emit(currentSelectedAssets);
            } else {
                this.firstTimeLoading = true;
            }
        } else {
            // Used to remember data from form after closing the cart and re-opening
            if (this.currentLessonForm) {
                this.lessonForm.patchValue({
                    displayName: this.currentLessonForm.displayName,
                    description: this.currentLessonForm.description,
                    scorePossible: this.currentLessonForm.scorePossible,
                    visibility: this.currentLessonForm.visibility,
                    levelIds: this.currentLessonForm.levelIds + "",
                    unit: this.currentLessonForm.unit,
                });
            }

            this.lessonForm.patchValue({
                publishedBy: this.userGUID,
                subjectId: this.subjectId,
                audit: {
                    createdBy: this.userGUID,
                },
            });
        }

        this.setValidUnitsAndLevelsForSubjectId(this.subjectId);
        this.lessonIcon = this.languageHelper.ifMathLanguage(this.editionId)
            ? "lesson-math"
            : "lesson";
    }

    onLanguageChange(event: MatSelectChange) {
        this.setValidUnitsAndLevelsForSubjectId(event.value);
    }

    setValidUnitsAndLevelsForSubjectId(subjectId = ""): void {
        const sId: SubjectId = subjectId || this.subjectId;
        this.formHelper.setSubjectAndEdition(
            sId,
            this.editionId,
            this.activePrograms,
            this.activeEditions
        );

        // levels
        this.lessonLevels = this.formHelper.getFormLevels();
        if (this.editingLesson && this.currentLessonForm?.editionIds?.length) {
            // Here is to limit the level for cloned or editing existed asset. It should not run while creating a new asset
            const levels = this.formHelper.getFormLevelsForAssetsLevelIds(
                this.currentLessonForm?.levelIds
            );
            if (levels.length) {
                this.lessonLevels = [];
                this.currentLessonForm?.editionIds.forEach((e) => {
                    this.formHelper.setSubjectAndEdition(
                        sId,
                        e,
                        this.activePrograms,
                        this.activeEditions
                    );
                    Array.prototype.push.apply(this.lessonLevels, levels);
                });
            }
        }

        // units
        this.lessonUnits = this.formHelper.getFormUnits();
    }

    onSubmit() {
        if (
            this.lessonForm.valid &&
            this.selectedAssets &&
            this.selectedAssets.assets.length > 0 &&
            !this.working
        ) {
            this.working = true;
            if (!this.exists) {
                this.create.emit(this.lessonForm.value);
            } else {
                this.lessonForm.value.assetId = this.lessonAssetId;
                this.update.emit(this.lessonForm.value);
            }
        }
    }

    disableForm(lessonForm: any) {
        // It doesn't look like this is called anywhere in the code base
        if (
            lessonForm.invalid ||
            lessonForm.pristine ||
            !this.selectedAssets ||
            this.selectedAssets.assets.length === 0 ||
            this.working
        ) {
            return true;
        }
    }

    getFormData() {
        let formValidity = false;
        if (!this.componentInit && this.editingLesson) {
            this.componentInit = true;
        } else {
            formValidity = this.lessonForm.valid;
        }
        if (this.lessonForm) {
            this.setLessonFormValidity.emit(formValidity);
            if (this.exists) {
                this.lessonForm.value.assetId = this.lessonAssetId;
            }
            this.setLessonFormData.emit(this.lessonForm.value);
        }
    }

    cancelLesson() {
        this.cancel.emit();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
